import { useState, useMemo, useEffect, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient, track } from "@mydailydirect/app-kit";
import {
  Badge,
  Button,
  Card,
  formatPaise,
  useToast,
  ImageLightboxModal,
  inferWeightKg,
  InteractiveLoader,
  getVolumeTiers,
  calculateEffectiveTierPrice,
  hasActiveQpsScheme,
  activePacks,
  resolvePack,
  packView,
  scrollToTop,
} from "@mydailydirect/ui";
import {
  ArrowLeft,
  ShoppingCart,
  Share2,
  Minus,
  Plus,
  Check,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Scale,
  Sparkles,
  Truck,
  Store,
  TrendingUp,
  Receipt,
  MessageCircle,
  AlertCircle,
  Tag,
  Layers,
} from "lucide-react";
import { useCart } from "../lib/cart";

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const skuId = Number(id);
  const navigate = useNavigate();
  const client = useMyDailyDirectClient();
  const cart = useCart();
  const { toast } = useToast();

  // Load SKUs list
  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
  });

  const sku = useMemo(() => {
    return skusQuery.data?.find((s) => s.id === skuId);
  }, [skusQuery.data, skuId]);

  useEffect(() => {
    if (sku?.id) {
      track("view_item", {
        skuId: sku.id,
        valuePaise: sku.pricePaise,
        propsJson: {
          name: sku.name,
          category: sku.category,
          vendorId: sku.vendorId,
        },
      });
    }
  }, [sku?.id]);

  // Always reset view to top of page when navigating to PDP or changing SKU
  useLayoutEffect(() => {
    scrollToTop();
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });
    return () => cancelAnimationFrame(rafId);
  }, [skuId]);

  // Active photo gallery index
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const openCart = () => window.dispatchEvent(new CustomEvent("mdd:open-cart"));

  // Assortments (Pack sizes)
  const packs = useMemo(() => (sku ? activePacks(sku) : []), [sku]);
  const defaultP = useMemo(() => (sku ? resolvePack(sku) : null), [sku]);
  const [selectedPackId, setSelectedPackId] = useState<number | null>(() => defaultP?.id ?? null);

  useEffect(() => {
    if (defaultP && selectedPackId === null) {
      setSelectedPackId(defaultP.id ?? null);
    }
  }, [defaultP]);

  const currentPack = useMemo(
    () => (selectedPackId != null ? packs.find((p) => p.id === selectedPackId) ?? defaultP : defaultP),
    [packs, selectedPackId, defaultP]
  );

  const packV = useMemo(() => (sku ? packView(sku, currentPack) : null), [sku, currentPack]);

  // Selected quantity stepper for this PDP
  const currentCartQty = sku && sku.id !== null ? cart.qtyOf(sku.id, currentPack?.id ?? null) : 0;
  const [selectedQty, setSelectedQty] = useState(1);

  // Sync selectedQty with cart quantity if already in cart
  useEffect(() => {
    if (currentCartQty > 0) {
      setSelectedQty(currentCartQty);
    } else {
      setSelectedQty(1);
    }
  }, [currentCartQty, selectedPackId]);

  // Related products from same category
  const relatedSkus = useMemo(() => {
    if (!sku || !skusQuery.data) return [];
    return skusQuery.data
      .filter((s) => s.id !== sku.id && s.active && s.category === sku.category)
      .slice(0, 4);
  }, [skusQuery.data, sku]);

  // Fallback if loading
  if (skusQuery.isLoading) {
    return (
      <div className="py-12 flex items-center justify-center min-h-[60vh]">
        <InteractiveLoader
          title="Loading Commodity Details"
          subtitle="Fetching daily mandi rates, packaging options and live stock..."
        />
      </div>
    );
  }

  // Not Found state
  if (!sku) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Product Not Found</h2>
        <p className="text-sm text-muted-foreground">
          This wholesale SKU might be out of stock, uncataloged, or the link may be incorrect.
        </p>
        <Button variant="primary" onClick={() => navigate("/")} className="font-bold">
          <Store className="w-4 h-4 mr-2" /> Back to Wholesale Catalog
        </Button>
      </div>
    );
  }

  // Weight, Per-KG rate, MRP, and Profit Calculations (adapted for active assortment)
  const effectivePricePaise = packV?.pricePaise ?? sku.pricePaise;
  const effectiveMrpPaise = packV?.mrpPaise ?? (sku.mrpPaise && sku.mrpPaise > 0 ? sku.mrpPaise : 0);
  const effectiveWeightKg =
    packV?.weightKg && packV.weightKg > 0
      ? packV.weightKg
      : sku.weightKg && sku.weightKg > 0
      ? sku.weightKg
      : inferWeightKg(sku.name, sku.unit);
  const hasWeight = effectiveWeightKg > 0;
  const pricePerKgPaise = hasWeight ? Math.round(effectivePricePaise / effectiveWeightKg) : null;

  const mrpPaise = effectiveMrpPaise;
  const hasSavings = mrpPaise > effectivePricePaise;
  const mrpSavingsPaise = hasSavings ? mrpPaise - effectivePricePaise : 0;
  const mrpSavingsPercent = hasSavings ? Math.round(((mrpPaise - effectivePricePaise) / mrpPaise) * 100) : 0;

  // Retailer profit metrics
  const profitPerUnitPaise = mrpSavingsPaise;
  const totalProfitForSelectedQtyPaise = profitPerUnitPaise * selectedQty;
  const profitPerKgPaise = hasWeight && hasSavings ? Math.round(mrpSavingsPaise / effectiveWeightKg) : null;

  // Media
  const photos = Array.isArray(sku.photoUrls) && sku.photoUrls.length > 0
    ? sku.photoUrls
    : (sku as any).photoUrl ? [(sku as any).photoUrl] : [];
  const hasMultiplePhotos = photos.length > 1;
  const activePhoto = photos[activePhotoIdx] || "/placeholder-product.svg";

  // Stock availability based on active pack units
  const availablePackQty = packV?.availableQty ?? sku.availableQty;
  const outOfStock = availablePackQty <= 0;
  const isLowStock = !outOfStock && availablePackQty <= 20;

  // Category tags chips (like competitor screen)
  const categoryTags = (() => {
    const list: string[] = [];
    if (sku.category) {
      // Split category if it has slashes or commas
      const parts = sku.category.split(/[/,>]/).map((s) => s.trim()).filter(Boolean);
      list.push(...parts);
    }
    if (list.length === 0) list.push("Staples");
    // Add sub-tag if name has keywords
    const lower = sku.name.toLowerCase();
    if (lower.includes("rice") && !list.some((c) => c.toLowerCase().includes("rice"))) list.push("Rice");
    if (lower.includes("raw") && !list.some((c) => c.toLowerCase().includes("raw"))) list.push("Raw Rice");
    if (lower.includes("oil") && !list.some((c) => c.toLowerCase().includes("oil"))) list.push("Edible Oil");
    if (lower.includes("dal") || lower.includes("pulse")) list.push("Pulses");
    return Array.from(new Set(list));
  })();

  // Handlers
  const handleAddToCart = () => {
    if (sku.id === null) return;
    cart.setQty(sku.id, selectedQty, currentPack?.id ?? null);
    toast({
      title: "Added to Cart!",
      description: `${selectedQty} × ${sku.name}${packV?.label ? ` (${packV.label})` : ""} added to your wholesale cart.`,
      variant: "success",
    });
  };

  const handleBuyNow = () => {
    if (sku.id === null) return;
    cart.setQty(sku.id, selectedQty, currentPack?.id ?? null);
    openCart();
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    const packStr = packV?.label ? ` (${packV.label})` : "";
    const shareText = `🌾 Wholesale Deal on My Daily Direct:\n📦 ${sku.name}${packStr}\n💰 Wholesale Rate: ${formatPaise(effectivePricePaise)}${pricePerKgPaise ? ` (₹${(pricePerKgPaise / 100).toFixed(2)}/kg)` : ''}\n${hasSavings ? `🏷️ MRP: ${formatPaise(mrpPaise)} (Earn ${formatPaise(mrpSavingsPaise)} Profit!)\n` : ''}🚚 Direct Morning Delivery to your Kirana Store!\n👉 Order now: ${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title: sku.name,
        text: shareText,
        url: shareUrl,
      }).catch(() => {});
    } else {
      // Open WhatsApp direct share
      const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
      window.open(waUrl, "_blank");
    }
  };

  // Helper for NA fallback as requested by user
  const displayOrNA = (val?: string | number | null) => {
    if (val === undefined || val === null || val === "" || String(val).trim() === "") {
      return "NA";
    }
    return String(val);
  };

  const handleGoBack = () => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      typeof window.history.state.idx === "number" &&
      window.history.state.idx > 0
    ) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3 sm:py-6 space-y-6 pb-32">
      {/* Top Header: Back Arrow + Breadcrumb + Share + Live Cart */}
      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleGoBack}
            className="h-9 w-9 rounded-full -ml-1 text-foreground hover:bg-muted"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {/* Breadcrumb text */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground truncate font-medium">
            <span
              onClick={() => navigate("/")}
              className="hover:text-primary cursor-pointer transition-colors"
            >
              Catalog
            </span>
            <span>/</span>
            <span className="truncate text-foreground font-semibold">
              {sku.category || "All Items"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Share Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="h-9 px-2.5 rounded-xl text-xs font-semibold gap-1.5 hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Share Wholesale Deal"
          >
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>

          {/* Cart Icon with Live Badge Count */}
          <Button
            variant="outline"
            size="sm"
            onClick={openCart}
            className="h-9 px-3 rounded-xl font-bold text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/10 relative"
            title="View Cart"
          >
            <ShoppingCart className="h-4 w-4" />
            <span>Cart</span>
            {cart.totalItems > 0 && (
              <span className="h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-black flex items-center justify-center -mr-1">
                {cart.totalItems}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Product Title (Prominent like competitor screen) */}
      <div className="space-y-2">
        <h1 className="text-lg sm:text-2xl font-black text-foreground tracking-tight leading-snug">
          {sku.name}
        </h1>

        {/* Category Tags Chips (Design System Tokenized) */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {categoryTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-control text-xs font-bold border border-border bg-sunken text-foreground transition-colors"
            >
              <Check className="h-3.5 w-3.5 text-primary stroke-[3]" />
              {tag}
            </span>
          ))}

          <Badge variant="secondary" className="text-2xs font-mono font-semibold py-1">
            SKU: {sku.code}
          </Badge>
        </div>
      </div>

      {/* Main Layout Grid: Hero Gallery + Pricing & Order CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        {/* Left Column: Multi-Image Showcase Gallery */}
        <div className="md:col-span-6 space-y-3">
          <Card className="relative overflow-hidden rounded-3xl border-border bg-card shadow-sm p-2 group">
            {/* Main Stage Image */}
            <div
              className="relative aspect-square w-full rounded-2xl overflow-hidden bg-muted/20 flex items-center justify-center cursor-pointer"
              onClick={() => setIsLightboxOpen(true)}
            >
              <img
                src={activePhoto}
                alt={sku.name}
                className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder-product.svg";
                }}
              />

              {/* Enlarge Zoom Overlay Icon */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLightboxOpen(true);
                }}
                className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/60 hover:bg-black/90 active:scale-95 text-white flex items-center justify-center transition-all opacity-80 sm:opacity-0 group-hover:opacity-100 shadow-md z-10"
                title="Click to Zoom Fullscreen"
              >
                <Maximize2 className="h-4 w-4" />
              </button>

              {/* Previous / Next Arrow Controls */}
              {hasMultiplePhotos && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
                    }}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center transition-all opacity-90 shadow-md z-10 hover:scale-110"
                    aria-label="Previous Photo"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePhotoIdx((prev) => (prev + 1) % photos.length);
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center transition-all opacity-90 shadow-md z-10 hover:scale-110"
                    aria-label="Next Photo"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}

              {/* Photo Counter Pill */}
              {hasMultiplePhotos && (
                <span className="absolute bottom-3 right-3 rounded-md bg-black/70 backdrop-blur-xs px-2 py-0.5 text-2xs font-bold text-white z-10">
                  {activePhotoIdx + 1} / {photos.length}
                </span>
              )}

              {/* Discount Badge on Image */}
              {hasSavings && !outOfStock && (
                <span className="absolute top-3 left-3 rounded-lg bg-emerald-600 text-white shadow-md px-2 py-0.5 text-xs font-black tracking-wide z-10">
                  {mrpSavingsPercent}% OFF
                </span>
              )}

              {/* Net Weight / Assortment Pill on Image */}
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5 z-10">
                {packV?.label ? (
                  <span className="rounded-lg bg-primary text-primary-foreground border border-primary/20 px-2 py-1 text-xs font-extrabold flex items-center gap-1 shadow-xs">
                    <Layers className="h-3.5 w-3.5" />
                    {packV.label}
                  </span>
                ) : hasWeight ? (
                  <span className="rounded-lg bg-background/95 backdrop-blur-xs border border-border px-2 py-1 text-xs font-extrabold text-foreground flex items-center gap-1 shadow-xs">
                    <Scale className="h-3.5 w-3.5 text-primary" />
                    {effectiveWeightKg} kg
                  </span>
                ) : null}
                {sku.unit && !packV?.label && (
                  <span className="rounded-lg bg-background/95 backdrop-blur-xs border border-border px-2 py-1 text-xs font-semibold text-muted-foreground shadow-xs">
                    {sku.unit}
                  </span>
                )}
              </div>

              {/* Out of Stock Overlay */}
              {outOfStock && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-xs flex items-center justify-center z-20">
                  <Badge variant="destructive" className="text-sm font-bold px-3 py-1.5 shadow-md">
                    Out of Stock
                  </Badge>
                </div>
              )}
            </div>

            {/* Thumbnail Gallery Strip */}
            {hasMultiplePhotos && (
              <div className="flex items-center gap-2 pt-2 px-1 overflow-x-auto pb-1">
                {photos.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActivePhotoIdx(idx)}
                    className={`relative h-14 w-14 shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
                      idx === activePhotoIdx
                        ? "border-primary shadow-xs scale-105"
                        : "border-border/60 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={url}
                      alt={`Thumbnail ${idx + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Wholesale Direct Sourcing Banner */}
          <div className="rounded-card border border-tone-success-border bg-tone-success-soft p-3.5 flex items-center gap-3 text-xs">
            <div className="w-8 h-8 rounded-control bg-tone-success-border/40 text-tone-success-text flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <span className="font-extrabold text-tone-success-text block">
                Trusted & Quality-Tested Sourcing Assured
              </span>
              <span className="text-muted-foreground text-2xs">
                Sourced from trusted, reliable mills with strict QC & quality testing on every batch.
              </span>
            </div>
          </div>
        </div>

        {/* Right Column: Pricing, Stepper, CTAs & Kirana Profit Calculator */}
        <div className="md:col-span-6 space-y-5">
          {/* Main Price & Stock Card */}
          <div className="space-y-3 p-4 sm:p-5 rounded-3xl bg-card border border-border shadow-xs">
            {/* Price Row: Big Selling Price + Per-KG Rate + Share */}
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
                  {formatPaise(effectivePricePaise)}
                </span>
                {packV?.label && (
                  <span className="text-xs sm:text-sm font-bold text-muted-foreground self-center">
                    / {packV.label}
                  </span>
                )}
                {pricePerKgPaise && (
                  <span className="text-sm sm:text-base font-extrabold text-tone-warning-text type-num">
                    ₹{(pricePerKgPaise / 100).toFixed(2)}/kg
                  </span>
                )}
              </div>

              {/* Share Icon */}
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors p-1"
                title="Share Deal"
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>
            </div>

            {/* MRP & Savings line */}
            {mrpPaise > 0 && (
              <div className="flex items-center gap-3 text-xs sm:text-sm">
                <span className="text-muted-foreground line-through font-medium">
                  M.R.P.: {formatPaise(mrpPaise)}
                </span>
                {hasSavings && (
                  <span className="font-extrabold text-tone-success-text type-num">
                    Save {formatPaise(mrpSavingsPaise)} ({mrpSavingsPercent}% OFF)
                  </span>
                )}
              </div>
            )}

            {/* MOQ & Availability Row (Competitor matching) */}
            <div className="flex items-center gap-3 text-xs font-semibold pt-1">
              <span className="text-foreground">
                {packV?.label ? "Assortment: " : "MOQ: "}
                <strong className="font-mono">{packV?.label ? packV.label : (sku.moq ?? 1)}</strong>
              </span>
              <span className="text-muted-foreground">·</span>
              {outOfStock ? (
                <span className="text-destructive font-bold">Out of Stock</span>
              ) : isLowStock ? (
                <span className="text-tone-warning-text font-bold">
                  ({availablePackQty} {packV?.label ? "packs" : "units"} left)
                </span>
              ) : (
                <span className="text-tone-success-text font-bold">
                  ({availablePackQty} {packV?.label ? "packs" : "units"} in stock)
                </span>
              )}
            </div>

            {/* Assortment / Pack Size Selector Chips */}
            {packs.length > 0 && (
              <div className="pt-2 border-t border-border/60 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-foreground flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-primary" /> Choose Assortment / Pack Size:
                  </span>
                  <span className="text-2xs font-semibold text-muted-foreground">
                    {packs.length} Options
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {packs.map((p) => {
                    const isSelected = p.id === currentPack?.id;
                    const pView = packView(sku, p);
                    return (
                      <button
                        key={p.id ?? p.label}
                        type="button"
                        onClick={() => setSelectedPackId(p.id ?? null)}
                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                          isSelected
                            ? "border-primary bg-primary/10 ring-2 ring-primary text-foreground shadow-2xs"
                            : "border-border bg-sunken/60 hover:bg-muted text-foreground"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-extrabold text-xs">{p.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-primary stroke-[3]" />}
                        </div>
                        <div className="mt-1 flex items-baseline justify-between text-xs">
                          <span className="font-black text-sm text-foreground type-num">
                            {formatPaise(pView.pricePaise)}
                          </span>
                          {pView.mrpPaise && pView.mrpPaise > pView.pricePaise && (
                            <span className="text-[10px] text-muted-foreground line-through type-num">
                              {formatPaise(pView.mrpPaise)}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Brand / Miller QPS Volume Slab Rates - ONLY shown if configured! */}
            {hasActiveQpsScheme(sku) && (
              <div className="p-3.5 rounded-2xl bg-muted/30 border border-border space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-foreground flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-primary" /> Miller / Brand QPS Scheme Slab Rates
                  </span>
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                    Scheme Slab Savings
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  {getVolumeTiers(sku).map((tier) => {
                    const isActive =
                      selectedQty >= tier.minQty && (tier.maxQty === undefined || selectedQty <= tier.maxQty);
                    return (
                      <div
                        key={tier.tierName}
                        className={`p-2 rounded-xl border transition-all ${
                          isActive
                            ? "border-primary bg-primary/10 shadow-2xs ring-1 ring-primary"
                            : "border-border bg-card"
                        }`}
                      >
                        <div className="text-[10px] font-semibold text-muted-foreground">{tier.tierName}</div>
                        <div className="font-extrabold text-foreground text-xs sm:text-sm mt-0.5">
                          {formatPaise(tier.pricePaise)}
                        </div>
                        <div className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                          {tier.discountPercent > 0 ? `Save ${tier.discountPercent}%` : "Base Rate"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity Stepper */}
            <div className="pt-2">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center rounded-2xl border-2 border-primary/20 bg-muted/20 p-1">
                  <button
                    type="button"
                    disabled={outOfStock || selectedQty <= (sku.moq ?? 1)}
                    onClick={() => setSelectedQty((prev) => Math.max(sku.moq ?? 1, prev - 1))}
                    className="h-10 w-11 rounded-xl bg-primary text-primary-foreground font-extrabold flex items-center justify-center active:scale-95 disabled:opacity-40 transition-all hover:bg-primary/90"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4 stroke-[3]" />
                  </button>

                  <input
                    type="number"
                    min={sku.moq ?? 1}
                    max={availablePackQty}
                    value={selectedQty}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= (sku.moq ?? 1)) {
                        setSelectedQty(Math.min(val, availablePackQty || 9999));
                      }
                    }}
                    className="w-14 text-center font-black text-base text-foreground bg-transparent border-0 focus:outline-hidden select-none"
                    aria-label="Quantity"
                  />

                  <button
                    type="button"
                    disabled={outOfStock || selectedQty >= availablePackQty}
                    onClick={() => setSelectedQty((prev) => Math.min(availablePackQty, prev + 1))}
                    className="h-10 w-11 rounded-xl bg-primary text-primary-foreground font-extrabold flex items-center justify-center active:scale-95 disabled:opacity-40 transition-all hover:bg-primary/90"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4 stroke-[3]" />
                  </button>
                </div>

                <div className="text-xs text-muted-foreground ml-1">
                  <span>Unit: <strong>{packV?.label || sku.unit || "Bag"}</strong></span>
                  {hasWeight && <span className="block font-mono text-2xs">({(selectedQty * effectiveWeightKg).toFixed(1)} kg total)</span>}
                </div>
              </div>
            </div>

            {/* Dual Action Buttons: Buy Now + Add to Cart (CTA styled) */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={outOfStock}
                onClick={handleBuyNow}
                className="h-12 rounded-control font-black text-sm active:scale-98 transition-all shadow-xs border-primary text-primary hover:bg-primary/5"
              >
                Buy Now
              </Button>

              <Button
                type="button"
                variant="primary"
                size="lg"
                disabled={outOfStock}
                onClick={handleAddToCart}
                className="h-12 rounded-control font-black text-sm active:scale-98 transition-all shadow-md bg-cta text-cta-foreground hover:opacity-95"
              >
                Add to Cart
              </Button>
            </div>

            {/* Live Total Calculation Row with Bulk Savings */}
            {(() => {
              const tier = calculateEffectiveTierPrice(sku, selectedQty);
              const discount = tier.discountPercent / 100;
              const lineTotal = Math.round(effectivePricePaise * selectedQty * (1 - discount));
              const savingsTotal = Math.round(effectivePricePaise * selectedQty * discount);
              return (
                <div className="pt-2 border-t border-border/60 space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-muted-foreground">Total:</span>
                    <span className="text-lg font-black text-foreground">
                      {formatPaise(lineTotal)}
                    </span>
                  </div>
                  {savingsTotal > 0 && (
                    <div className="flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400 font-bold">
                      <span>QPS Scheme ({tier.discountPercent}% off applied)</span>
                      <span>Save {formatPaise(savingsTotal)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Next-Day Delivery Timing Promise */}
            <div className="p-2.5 rounded-control bg-tone-success-soft border border-tone-success-border text-tone-success-text text-xs flex items-center gap-2 font-semibold">
              <Truck className="h-4 w-4 shrink-0" />
              <span>Order before 6 PM to get morning delivery next day</span>
            </div>

            {/* Sold By Info (Competitor matching: Sold By: EKALAVYA TRADERS) */}
            <div className="pt-1 text-xs">
              <span className="font-extrabold text-foreground">Sold By: </span>
              <span className="text-primary font-bold tracking-wide">
                MY DAILY DIRECT WHOLESALE
              </span>
              <span className="text-muted-foreground block text-2xs mt-0.5">
                Trusted Sourcing · Rigorous QC & Quality Tested · FSSAI Certified
              </span>
            </div>
          </div>

          {/* 💥 THE KILLER FEATURE: Kirana Retailer Resale Profit Calculator */}
          <Card className="overflow-hidden rounded-3xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card shadow-sm">
            <div className="p-4 sm:p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                    <TrendingUp className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h3 className="font-black text-sm text-foreground">Kirana Resale Profit Calculator</h3>
                    <p className="text-2xs text-muted-foreground">Your estimated margin reselling in your retail store</p>
                  </div>
                </div>

                {hasSavings && (
                  <Badge variant="success" className="font-black text-2xs uppercase">
                    {mrpSavingsPercent}% Margin
                  </Badge>
                )}
              </div>

              {/* Profit Metrics Breakdown Grid */}
              <div className="grid grid-cols-3 gap-2.5 pt-1 text-center">
                <div className="p-2.5 rounded-2xl bg-muted/40 border border-border">
                  <span className="text-2xs text-muted-foreground block font-medium">Your Cost</span>
                  <span className="font-extrabold text-xs sm:text-sm text-foreground block mt-0.5">
                    {formatPaise(sku.pricePaise)}
                  </span>
                </div>

                <div className="p-2.5 rounded-2xl bg-muted/40 border border-border">
                  <span className="text-2xs text-muted-foreground block font-medium">Consumer MRP</span>
                  <span className="font-extrabold text-xs sm:text-sm text-foreground block mt-0.5">
                    {mrpPaise > 0 ? formatPaise(mrpPaise) : "Open MRP"}
                  </span>
                </div>

                <div className="p-2.5 rounded-control bg-tone-success-soft border border-tone-success-border">
                  <span className="text-2xs text-tone-success-text block font-bold">Profit / Unit</span>
                  <span className="font-black text-xs sm:text-sm text-tone-success-text block mt-0.5 type-num">
                    {hasSavings ? formatPaise(profitPerUnitPaise) : "Wholesale Margin"}
                  </span>
                </div>
              </div>

              {/* Dynamic Quantity Return Banner */}
              {hasSavings && (
                <div className="p-3 rounded-control bg-primary text-primary-foreground shadow-xs text-xs space-y-0.5">
                  <div className="flex items-center justify-between font-extrabold">
                    <span>Your Total Profit for {selectedQty} {selectedQty === 1 ? 'pack' : 'packs'}:</span>
                    <span className="text-base font-black text-cta type-num">
                      +{formatPaise(totalProfitForSelectedQtyPaise)}
                    </span>
                  </div>
                  {profitPerKgPaise && (
                    <div className="text-2xs opacity-90">
                      You make approximately <strong>₹{(profitPerKgPaise / 100).toFixed(2)} pure profit per KG</strong>!
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Quick WhatsApp Deal Share Action Card */}
          <Button
            type="button"
            variant="outline"
            onClick={handleShare}
            className="w-full h-11 rounded-2xl border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-bold text-xs gap-2"
          >
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            Share Deal with Partner / Staff on WhatsApp
          </Button>
        </div>
      </div>

      {/* Description Section (Competitor matching: Description / RNR RAW) */}
      <Card className="rounded-3xl border-border bg-card p-5 sm:p-6 space-y-3">
        <div className="flex items-center gap-2 border-b border-border/60 pb-3">
          <Tag className="w-4 h-4 text-primary" />
          <h2 className="text-base font-black text-foreground">Description</h2>
        </div>
        <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
          {sku.description || sku.name || "NA"}
        </p>
      </Card>

      {/* Comprehensive Product Specifications Table (Graceful NA Fallbacks) */}
      <Card className="rounded-3xl border-border bg-card overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" />
            <h2 className="text-base font-black text-foreground">Product Specifications</h2>
          </div>
          <Badge variant="secondary" className="text-2xs font-semibold">
            Product No.: {sku.code}
          </Badge>
        </div>

        <div className="divide-y divide-border/60 text-xs sm:text-sm">
          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Product No. / SKU Code</span>
            <span className="font-mono font-bold text-foreground text-right sm:text-left">{sku.code}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Brand</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.brand)}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Category</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.category)}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Net Weight</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{hasWeight ? `${effectiveWeightKg} kg` : "NA"}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Unit / Pack</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.unit)}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Packaging Type</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.packagingType)}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Shelf Life / Expiry</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.shelfLife)}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Sourcing / Origin</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.origin || "Verified Partner Mills, India")}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">GST Slab Rate</span>
            <span className="font-semibold text-foreground text-right sm:text-left">GST {sku.gstPercent ?? 5}%</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Minimum Order Qty (MOQ)</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{sku.moq ?? 1}</span>
          </div>

          <div className="grid grid-cols-2 p-3.5 sm:px-6 hover:bg-muted/20 transition-colors">
            <span className="font-medium text-muted-foreground">Storage Instructions</span>
            <span className="font-semibold text-foreground text-right sm:text-left">{displayOrNA(sku.storageInstructions || "Store in a cool, dry place")}</span>
          </div>
        </div>
      </Card>

      {/* Retailers Also Bought Section (Cross-Sell / Recommendations) */}
      {relatedSkus.length > 0 && (
        <div className="space-y-3 pt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-black text-foreground">
              Retailers Also Ordered
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-xs font-bold text-primary"
            >
              View All <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {relatedSkus.map((item) => {
              const itemPhotos = Array.isArray(item.photoUrls) && item.photoUrls.length > 0
                ? item.photoUrls
                : (item as any).photoUrl ? [(item as any).photoUrl] : [];
              const itemPhoto = itemPhotos[0] || "/placeholder-product.svg";

              return (
                <Card
                  key={item.id}
                  onClick={() => {
                    navigate(`/product/${item.id}`);
                    scrollToTop();
                  }}
                  className="p-3 rounded-2xl border-border bg-card hover:border-primary/50 transition-all cursor-pointer flex flex-col justify-between group shadow-2xs hover:shadow-sm"
                >
                  <div className="space-y-2">
                    <div className="aspect-square w-full rounded-xl overflow-hidden bg-muted/20 flex items-center justify-center">
                      <img
                        src={itemPhoto}
                        alt={item.name}
                        className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <h3 className="font-bold text-xs text-foreground line-clamp-2 leading-tight">
                      {item.name}
                    </h3>
                  </div>

                  <div className="pt-2 flex items-center justify-between gap-1 mt-auto">
                    <span className="font-black text-xs text-foreground">
                      {formatPaise(item.pricePaise)}
                    </span>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.id !== null) cart.add(item.id);
                        toast({
                          title: "Added!",
                          description: `${item.name} added to cart`,
                          variant: "success",
                        });
                      }}
                      className="h-7 w-7 p-0 rounded-lg"
                      title="Quick Add"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Sticky Bottom Bar on Mobile Viewports (Positioned cleanly above mobile bottom nav) */}
      <div className="fixed inset-x-0 bottom-14 z-30 md:hidden bg-card/95 backdrop-blur-md border-t border-border px-3.5 py-2.5 shadow-overlay flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="text-2xs uppercase font-extrabold text-muted-foreground block">
            Total Payable
          </span>
          <span className="text-base font-black text-foreground type-num">
            {formatPaise(selectedQty * effectivePricePaise)}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex items-center rounded-control border border-border bg-sunken p-0.5">
            <button
              type="button"
              disabled={outOfStock || selectedQty <= (sku.moq ?? 1)}
              onClick={() => setSelectedQty((prev) => Math.max(sku.moq ?? 1, prev - 1))}
              className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-control bg-card text-foreground font-bold flex items-center justify-center active:scale-95 disabled:opacity-40"
              aria-label="Decrease quantity"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-8 text-center text-xs font-black select-none type-num">
              {selectedQty}
            </span>
            <button
              type="button"
              disabled={outOfStock || selectedQty >= availablePackQty}
              onClick={() => setSelectedQty((prev) => Math.min(availablePackQty, prev + 1))}
              className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-control bg-card text-foreground font-bold flex items-center justify-center active:scale-95 disabled:opacity-40"
              aria-label="Increase quantity"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={outOfStock}
            onClick={handleBuyNow}
            className="h-10 px-4 rounded-control font-black text-xs shadow-sm bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99]"
          >
            Buy Now
          </Button>
        </div>
      </div>

      {/* Enlarged Fullscreen Lightbox Modal */}
      {isLightboxOpen && photos.length > 0 && (
        <ImageLightboxModal
          open={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          photos={photos}
          initialIndex={activePhotoIdx}
          title={sku.name}
          subtitle={`SKU: ${sku.code} · ${hasWeight ? `${effectiveWeightKg} kg` : ''} ${sku.unit ? `(${sku.unit})` : ''}`}
        />
      )}
    </div>
  );
}
